import { NavLink, Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { usePortalAuth } from "@/lib/portalAuth";
import { useBranding, assetUrl } from "@/lib/branding";
import {
  LayoutDashboard, FileText, Package, Receipt, FolderDown, LifeBuoy, Boxes, Truck, BookOpen,
  LogOut, Menu, ChevronsLeft, ChevronLeft, ChevronRight
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PortalAiAssistant } from "@/components/PortalAiAssistant";
import { InstallBanner } from "@/components/InstallBanner";
import { IntroSplash } from "@/components/IntroSplash";
import { PortalOnboarding } from "@/components/PortalOnboarding";
import { Sparkles, Volume2, VolumeX } from "lucide-react";
import { isSoundMuted, setSoundMuted } from "@/lib/notifySound";

const NAV = [
  { to: "/portal", label: "Dashboard", sub: "Overview & Updates", icon: LayoutDashboard, end: true },
  { to: "/portal/catalogue", label: "Catalogue", sub: "Browse Products", icon: Boxes },
  { to: "/portal/quotations", label: "Quotations", sub: "Quotes & Approvals", icon: FileText },
  { to: "/portal/orders", label: "Orders", sub: "Track Your Orders", icon: Package },
  { to: "/portal/dispatch", label: "Dispatch", sub: "Live Shipments", icon: Truck },
  { to: "/portal/invoices", label: "Invoices", sub: "Bills & Payments", icon: Receipt },
  { to: "/portal/ledger", label: "Ledger", sub: "Account Statement", icon: BookOpen },
  { to: "/portal/documents", label: "Documents", sub: "Certificates & Files", icon: FolderDown },
  { to: "/portal/support", label: "Support", sub: "Raise a Ticket", icon: LifeBuoy },
];

const iconBtn = "p-2 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors duration-200";

export default function PortalLayout({ children }) {
  const { customer, logout } = usePortalAuth();
  const { branding } = useBranding() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [showIntro, setShowIntro] = useState(() => sessionStorage.getItem("hts_intro") === "1");
  const closeIntro = () => { sessionStorage.removeItem("hts_intro"); setShowIntro(false); };
  const [muted, setMuted] = useState(isSoundMuted());
  const [showTour, setShowTour] = useState(() => !localStorage.getItem("portal_welcome_v1"));
  const [collapsed, setCollapsed] = useState(localStorage.getItem("portal_sidebar_collapsed") === "1");
  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem("portal_sidebar_collapsed", c ? "0" : "1");
      return !c;
    });
  };

  if (customer === null) return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm bg-[#F4F6FB]">Loading…</div>;
  if (customer === false) return <Navigate to="/portal/login" replace />;

  const companyName = branding?.company_name || "Hi-Tech Silico";
  const companyInitials = companyName.split(/[\s-]+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const sidebarLogo = assetUrl(branding?.sidebar_logo_url);
  const sidebarLogoDark = assetUrl(branding?.sidebar_logo_dark_url);
  const collapsedLogo = assetUrl(branding?.sidebar_logo_collapsed_url);
  const initials = (customer?.company_name || "C").split(/[\s-]+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const active = NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to) && n.to !== "/portal"));
  const pageTitle = active?.label || "Client Portal";
  const pageSub = active?.sub || "Your live business view";

  const doLogout = async () => {
    await logout();
    navigate("/portal/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F4F6FB]" data-testid="portal-layout">
      {showIntro && <IntroSplash onDone={closeIntro} />}
      {/* Sidebar */}
      <aside
        className={`fixed lg:relative inset-y-0 left-0 z-40 w-[264px] ${collapsed ? "lg:w-[80px]" : "lg:w-[264px]"} border-r border-[#EAEDF3] bg-white flex flex-col transform text-slate-600 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-all duration-200`}
        data-testid="portal-sidebar"
      >
        <div className={`h-[74px] border-b border-[#EAEDF3] flex items-center justify-between flex-none ${collapsed ? "lg:px-3 px-5" : "px-5"}`}>
          <Link to="/portal" className="min-w-0 flex-1 flex items-center gap-3" data-testid="portal-sidebar-logo">
            {collapsed ? (
              <span className="hidden lg:flex w-full justify-center">
                {collapsedLogo ? (
                  <img src={collapsedLogo} alt={companyName} className="h-10 w-10 object-contain rounded-xl" />
                ) : (
                  <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center text-sm font-display font-extrabold text-white shadow-[0_6px_18px_rgba(59,130,246,0.35)]">
                    {companyInitials}
                  </span>
                )}
              </span>
            ) : null}
            <span className={`flex items-center gap-3 min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
              {sidebarLogo || sidebarLogoDark ? (
                <img src={sidebarLogo || sidebarLogoDark} alt={companyName} className="h-[58px] max-w-[208px] object-contain" />
              ) : (
                <>
                  <span className="h-10 w-10 flex-none rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#2563EB] flex items-center justify-center text-sm font-display font-extrabold text-white shadow-[0_6px_18px_rgba(59,130,246,0.35)]">
                    {companyInitials}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[15px] font-display font-bold leading-tight truncate text-slate-900">{companyName}</span>
                    <span className="block text-[11px] truncate text-slate-400">Client Portal</span>
                  </span>
                </>
              )}
            </span>
          </Link>
          <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)} aria-label="close">
            <ChevronsLeft size={18} />
          </button>
        </div>
        <button
          onClick={toggleCollapsed}
          aria-label={collapsed ? "expand menu" : "collapse menu"}
          title={collapsed ? "Expand menu" : "Collapse menu"}
          data-testid="portal-sidebar-collapse-btn"
          className="hidden lg:flex absolute -right-3.5 top-[88px] z-50 w-7 h-7 p-0 flex-none leading-none rounded-full items-center justify-center bg-white text-[#3B82F6] border border-slate-200 shadow-md hover:shadow-lg hover:scale-110 active:scale-95 transition-[transform,box-shadow] duration-200"
        >
          {collapsed ? <ChevronRight size={14} strokeWidth={2.5} className="relative left-[0.5px]" /> : <ChevronLeft size={14} strokeWidth={2.5} className="relative -left-[0.5px]" />}
        </button>
        <nav className="flex-1 overflow-y-auto py-3 px-3 scroll-thin">
          {NAV.map(({ to, label, sub, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`pnav-${label.toLowerCase()}`}
              onClick={() => setOpen(false)}
              title={label}
              className={({ isActive }) =>
                `group flex items-center gap-3 my-1 rounded-xl transition-all duration-200 ${
                  collapsed ? "lg:justify-center lg:px-0 lg:py-2.5 px-3.5 py-2" : "px-3.5 py-2"
                } ${
                  isActive
                    ? "bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white shadow-[0_6px_18px_rgba(59,130,246,0.35)]"
                    : "text-slate-500 hover:bg-slate-100/80 hover:text-slate-800"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={17} strokeWidth={isActive ? 2 : 1.6} className="flex-none transition-transform duration-200 group-hover:scale-110" />
                  <span className={`min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
                    <span className={`block text-[13px] leading-tight truncate ${isActive ? "font-semibold text-white" : "font-medium text-slate-700"}`}>{label}</span>
                    <span className={`block text-[10.5px] leading-tight truncate ${isActive ? "text-white/70" : "text-slate-400"}`}>{sub}</span>
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className={`border-t border-[#EAEDF3] py-3 px-5 text-[10px] text-slate-500/80 leading-relaxed ${collapsed ? "lg:hidden" : ""}`}>
          ISO 9001:2015 · +91 8888837157<br />info@hitechsilico.com
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 backdrop-blur-xl border-b border-slate-200/80 bg-white/80 h-[74px] flex items-center gap-3 px-4 lg:px-6">
          <button className="lg:hidden mr-1 text-slate-700" onClick={() => setOpen(true)} aria-label="menu" data-testid="portal-menu-btn">
            <Menu size={20} />
          </button>
          <div className="min-w-0 flex-none max-w-[46vw] sm:max-w-none">
            <div className="flex items-center gap-2">
              <h1 className="text-[17px] font-display font-bold text-slate-900 leading-tight truncate">{pageTitle}</h1>
              <span className="hidden sm:inline-flex text-[9px] font-bold uppercase tracking-wider text-[#3B82F6] bg-[#3B82F6]/10 rounded-full px-2 py-0.5 items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-emerald-500 inline-block" /> Live
              </span>
            </div>
            <p className="text-[11px] text-slate-400 leading-tight truncate">{pageSub}</p>
          </div>

          <div className="flex-1 min-w-0" />

          <TooltipProvider delayDuration={150}>
            <div className="flex items-center gap-0.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => { setSoundMuted(!muted); setMuted(!muted); }} className={iconBtn} data-testid="portal-sound-toggle">
                    {muted ? <VolumeX size={16} strokeWidth={1.5} /> : <Volume2 size={16} strokeWidth={1.5} />}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">{muted ? "Unmute notification sounds" : "Mute notification sounds"}</TooltipContent>
              </Tooltip>
              <div className="hidden sm:block w-px h-5 mx-1.5 bg-slate-200" />
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold bg-gradient-to-br from-[#3B82F6] to-[#2563EB] text-white shadow-[0_4px_12px_rgba(59,130,246,0.35)]" data-testid="portal-avatar">
                    {initials}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">{customer?.company_name}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={doLogout} className={`${iconBtn} hover:text-rose-600 ml-1`} data-testid="portal-logout">
                    <LogOut size={16} strokeWidth={1.5} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">Sign out</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </header>
        <main className="flex-1 p-4 lg:p-6 max-w-[1600px] w-full mx-auto">
          <AnimatePresence mode="wait">
            <motion.div key={location.pathname}
                        initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        exit={{ opacity: 0, y: -14, filter: "blur(4px)" }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      {!aiOpen && (
        <button onClick={() => setAiOpen(true)} data-testid="portal-ai-launcher"
                className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full pl-4 pr-5 py-3 text-sm font-semibold shadow-[0_10px_30px_rgba(59,130,246,0.4)] hover:scale-105 transition-transform duration-200">
          <Sparkles size={15} className="text-[#C4B5FD]" /> Ask AI
        </button>
      )}
      <PortalAiAssistant open={aiOpen} onClose={() => setAiOpen(false)} />
      {showTour && <PortalOnboarding onFinish={() => setShowTour(false)} onOpenAi={() => setAiOpen(true)} />}
      <Toaster richColors position="top-right" />
      <InstallBanner />
    </div>
  );
}
